import { Injectable } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { AuthenticationService } from '../base/authentication.service';
import { User } from '../../interfaces/routering/user';
import { environment } from '../../../environments/environment';
import { Task } from '../../interfaces/task/task';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { ApiService } from '../base/api.service';
import { ServerRouteProperties } from '../../interfaces/base/server-route-properties';
import * as _ from 'lodash';
import { ServerResponse } from '../../interfaces/base/server.response';
import { TaskService } from './task.service';
import { take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { BroadcastService } from '../base/broadcast.service';
import { WebSocketResponse } from '../../interfaces/base/web-socket-response';
import { Department } from '../../interfaces/routering/department';

@Injectable()

export class TaskWebhookService {

  public taskUpdated: Subject<Task> = new Subject<Task>();
  public taskDeleted: Subject<Task> = new Subject<Task>();

  private user: User = null;
  private routeParams: ServerRouteProperties = {
    active: '',
    direction: '',
    filters: [],
  };

  private clearBroadcastEvents: string[] = [];
  private user$: Subscription = null;

  constructor(private authenticationService: AuthenticationService,
              private api: ApiService,
              private taskService: TaskService,
              private router: Router,
              private broadcastService: BroadcastService,
  ) {
    this.api.preSetFilters.pipe(take(1)).subscribe((): void => {
      if (this.api.filterPageName === 'tasks-open') {
        this.routeParams = JSON.parse(JSON.stringify(this.api.routeParams));
      }
    });

    this.api.filterUpdated.subscribe((): void => {
      if (this.api.filterPageName === 'tasks-open') {
        this.routeParams = JSON.parse(JSON.stringify(this.api.routeParams));
      }
    });
  }

  init(dataSource: MatTableDataSource<Task> = null, paginator: MatPaginator): void {
    if (this.user$ !== null) {
      this.user$.unsubscribe();
    }
    this.user$ = this.authenticationService.user$.subscribe((user: User): void => {
      if (user) {
        const i: number = 0;
        const interval: NodeJS.Timeout = setInterval((): void => {
          if (typeof window.Echo !== 'undefined') {
            if (!environment.production || this.user && this.user.role === 'super-admin') {
              console.log('TaskWebhookService ready');
            }

            this.destroy();

            this.clearBroadcastEvents.push('task.created');
            this.broadcastService.getEventSubject('task', 'created').subscribe((data: WebSocketResponse<Task>): void => {
              this.processData(user, paginator, dataSource, data.data, 'created');
            });

            this.clearBroadcastEvents.push('task.updated');
            this.broadcastService.getEventSubject('task', 'updated').subscribe((data: WebSocketResponse<Task>): void => {
              this.processData(user, paginator, dataSource, data.data, 'updated');
            });

            this.clearBroadcastEvents.push('task.deleted');
            this.broadcastService.getEventSubject('task', 'deleted').subscribe((data: WebSocketResponse<Task>): void => {
              this.processData(user, paginator, dataSource, data.data, 'deleted');
            });

            this.clearBroadcastEvents.push('task.reopened');
            this.broadcastService.getEventSubject('task', 'reopened').subscribe((data: WebSocketResponse<Task>): void => {
              this.processData(user, paginator, dataSource, data.data, 'reopened');
            });

            this.clearBroadcastEvents.push('task.resolved');
            this.broadcastService.getEventSubject('task', 'resolved').subscribe((data: WebSocketResponse<Task>): void => {
              this.processData(user, paginator, dataSource, data.data, 'resolved');
            });

            clearInterval(interval);
          } else {
            console.log('TaskWebhookService not ready');
          }
          if (i > 100) {
            clearInterval(interval);
          }
        }, 100);
      } else {
        this.destroy();
      }
    });
  }

  processData(user: User, paginator: MatPaginator, dataSource: MatTableDataSource<Task>, task: Task, type: 'created' | 'updated' | 'resolved' | 'reopened' | 'deleted'): void {
    if (!environment.production || this.user && this.user.role === 'super-admin') {
      console.log(this.routeParams);
    }

    const canTaskBeAdded: boolean = this.canTaskBeAdded(dataSource, task);
    if (!environment.production || this.user && this.user.role === 'super-admin') {
      console.log('canTaskBeAdded:', canTaskBeAdded);
    }

    // add a new task to appropriate user(s)
    if (type === 'created' && paginator.pageIndex === 0 && this.canTaskBeAddedForCurrentUser(user, task)) {
      // check if task can even be added because of filters
      if (canTaskBeAdded && this.canTaskBeAddedOnCurrentPage(dataSource, task)) {
        const index: number = dataSource.data.findIndex((d: Task): boolean => d.id === task.id);
        if (index === -1) {
          // if enough tasks, remove the last one
          if (dataSource.data.length >= this.api.pageSize) {
            if (!environment.production || this.user && this.user.role === 'super-admin') {
              console.log('removing the last task from the list');
            }
            dataSource.data.splice(-1, 1);
          }

          if (!environment.production || this.user && this.user.role === 'super-admin') {
            console.log('adding a task to the list');
          }
          dataSource.data.push(task);
        }
      }
    }

    // update existing task
    if ((type === 'updated' || type === 'reopened')) {
      this.taskUpdated.next(task);

      const index: number = dataSource.data.findIndex((d: Task): boolean => d.id === task.id);
      if (this.canTaskBeAddedForCurrentUser(user, task)) {
        // if a task exists, update it
        if (index !== -1) {
          if (!environment.production || this.user && this.user.role === 'super-admin') {
            console.log('task found, replace the data');
          }
          dataSource.data[index] = task;
        } else {
          // task not found, add it
          if (!environment.production || this.user && this.user.role === 'super-admin') {
            console.log('task not found, adding to the list');
          }

          dataSource.data.push(task);
        }
      } else {
        if (index !== -1) {
          if (!environment.production || this.user && this.user.role === 'super-admin') {
            console.log('task found, remove it');
          }
          dataSource.data.splice(index, 1);

          this.getMissingData(dataSource);
        }
      }
    }

    // remove processed task from list
    if (['resolved'].indexOf(type) !== -1) {
      const index: number = dataSource.data.findIndex((d: Task): boolean => d.id === task.id);
      if (this.canTaskBeAddedForCurrentUser(user, task)) {
        // task not found, add it
        if (!environment.production || this.user && this.user.role === 'super-admin') {
          console.log('task not found, adding to the list');
        }

        if (index !== -1) {
          dataSource.data[index] = task;
        } else {
          dataSource.data.push(task);
        }
      } else {
        if (index !== -1) {
          if (!environment.production || this.user && this.user.role === 'super-admin') {
            console.log('task found, remove it');
          }
          dataSource.data.splice(index, 1);

          this.getMissingData(dataSource);
        }
      }
    }

    // delete existing task
    if (type === 'deleted') {
      // task can be seen by the current user
      if (user.permissions.includes('tasks.deleted.read')) {
        const index: number = dataSource.data.findIndex((d: Task): boolean => d.id === task.id);
        if (index !== -1) {
          if (!environment.production || this.user && this.user.role === 'super-admin') {
            console.log('task found, replace the data');
          }
          // task deleted second time, presume force-delete and remove it from the list
          if (typeof dataSource.data[index].is_deleted !== 'undefined' && dataSource.data[index].is_deleted === true) {
            dataSource.data.splice(index, 1);

            this.getMissingData(dataSource);
          } else {
            // task deleted first time, update it
            dataSource.data[index] = task;
          }
        }

        this.taskDeleted.next(task);
      } else {
        // a task can't be seen by the current user
        const index: number = dataSource.data.findIndex((d: Task): boolean => d.id === task.id);
        if (index !== -1) {
          if (!environment.production || this.user && this.user.role === 'super-admin') {
            console.log('task found, remove it');
          }
          dataSource.data.splice(index, 1);

          this.getMissingData(dataSource);
        }
      }
    }

    if (canTaskBeAdded) {
      dataSource.data.sort((a: Task, b: Task): number => {
        let a1: string | number = null,
          b1: string | number = null,
          sortByReceivedAt: boolean = false;
        if (this.routeParams.active === 'created_at' || this.routeParams.active === '' || this.routeParams.active === null) {
          a1 = a.created_at;
          b1 = b.created_at;
        } else if (this.routeParams.active === 'basic_registration_id') {
          a1 = _.get(a, 'categories[0].basic_registration.name', null);
          b1 = _.get(b, 'categories[0].basic_registration.name', null);
          sortByReceivedAt = true;
        } else if (this.routeParams.active === 'task_registration_id') {
          a1 = _.get(a, 'categories[0].task_registration.name', null);
          b1 = _.get(b, 'categories[0].task_registration.name', null);
          sortByReceivedAt = true;
        } else if (this.routeParams.active === 'number') {
          a1 = a.number;
          b1 = b.number;
        } else if (this.routeParams.active === 'priority_id') {
          a1 = _.get(a, 'priority.display_name', null);
          b1 = _.get(b, 'priority.display_name', null);
          sortByReceivedAt = true;
        } else if (this.routeParams.active === 'assigned_id') {
          a1 = a.assigned_user.name.toUpperCase();
          b1 = b.assigned_user.name.toUpperCase();
          sortByReceivedAt = true;
        } else if (this.routeParams.active === 'handling_days') {
          a1 = _.get(a, 'handling.handling_days', null);
          b1 = _.get(b, 'handling.handling_days', null);
        } else if (this.routeParams.active === 'status_id') {
          a1 = _.get(a, 'status.display_name', null);
          b1 = _.get(b, 'status.display_name', null);
          sortByReceivedAt = true;
        }

        if (sortByReceivedAt) {
          if (this.routeParams.direction === 'desc' || this.routeParams.direction === '' || this.routeParams.direction === null) {
            return (b1 < a1 ? -1 : (b1 > a1 ? 1 : (b.created_at < a.created_at ? -1 : (b.created_at > a.created_at ? 1 : 0))));
          }

          return (a1 < b1 ? -1 : (a1 > b1 ? 1 : (b.created_at < a.created_at ? -1 : (b.created_at > a.created_at ? 1 : 0))));
        } else {
          if (this.routeParams.direction === 'desc' || this.routeParams.direction === '' || this.routeParams.direction === null) {
            return (b1 < a1 ? -1 : (b1 > a1 ? 1 : 0));
          }

          return (a1 < b1 ? -1 : (a1 > b1 ? 1 : 0));
        }
      });

      dataSource.data = dataSource.data;
    }
  }

  canTaskBeAdded(dataSource: MatTableDataSource<Task>, task: Task): boolean {
    const last: Task = _.last(dataSource.data);
    let response: boolean = true;

    // check if sorting is applied
    // if applied to text columns, get start and end 'cut-off' strings
    // check if task falls between those strings
    if (response) {
      if (typeof this.routeParams.active !== 'undefined' && this.routeParams.active !== null && this.routeParams.active !== '') {
        if (this.routeParams.active === 'created_at' && (
          this.routeParams.direction === 'asc' && task.created_at > _.get(last, 'created_at', null) && dataSource.data.length >= this.api.pageSize
        )) {
          response = false;
        }
        if (this.routeParams.active === 'basic_registration_id' && _.get(task, 'basic_registration.name', null) > _.get(last, 'basic_registration.name', null)) {
          response = false;
        }
        if (this.routeParams.active === 'task_registration_id' && _.get(task, 'task_registration.name', null) > _.get(last, 'task_registration.name', null)) {
          response = false;
        }
        if (this.routeParams.active === 'number' && typeof last !== 'undefined' && (
          this.routeParams.direction === 'asc' && task.number > last.number
        )) {
          response = false;
        }
        if (this.routeParams.active === 'priority_id' && typeof last !== 'undefined' && (
          this.routeParams.direction === 'asc' && task.priority_id !== last.priority_id
        )) {
          response = false;
        }
        if (this.routeParams.active === 'assigned_id' && (
          this.routeParams.direction === 'desc' && _.get(task, 'assigned.name') < _.get(last, 'assigned.name') ||
          this.routeParams.direction === 'asc' && _.get(task, 'assigned.name') > _.get(last, 'assigned.name')
        )) {
          response = false;
        }
        if (this.routeParams.active === 'handling_days' && (
          this.routeParams.direction === 'desc' && _.get(task, 'handling.handling_days') < _.get(last, 'handling.handling_days') ||
          this.routeParams.direction === 'asc' && _.get(task, 'handling.handling_days') > _.get(last, 'handling.handling_days')
        )) {
          response = false;
        }
        if (this.routeParams.active === 'status_id' && (
          this.routeParams.direction === 'desc' && _.get(task, 'status.display_name') < _.get(last, 'status.display_name') ||
          this.routeParams.direction === 'asc' && _.get(task, 'status.display_name') > _.get(last, 'status.display_name')
        )) {
          response = false;
        }
      }
      if (!environment.production || this.user && this.user.role === 'super-admin') {
        console.log('sorting judgement: ', response);
      }
    }

    // check if filtering is applied
    // if applied, check start and end cut-off strings
    if (response && this.routeParams.filters.length) {
      this.routeParams.filters.forEach(filter => {
        if (filter.field === 'created_at') {
          const date: string[] = (<string>filter.value).split('!');
          if (date[0] > task.created_at || date[1] < task.created_at) {
            response = false;
          }
        }
        if (filter.field === 'basic_registration_id' && _.get(task, 'basic_registration_id', null) !== filter.value) {
          response = false;
        }
        if (filter.field === 'task_registration_id' && _.get(task, 'task_registration_id', null) !== filter.value) {
          response = false;
        }
        if (filter.field === 'number' && task.number.indexOf(filter.value.toString()) !== 0) {
          response = false;
        }
        if (filter.field === 'assigned_id' && (filter.value === 'none' && task.assigned_user_id !== null || filter.value !== 'none' && task.assigned_user_id !== filter.value)) {
          response = false;
        }
        if (filter.field === 'status_id' && task.status_id !== filter.value) {
          response = false;
        }
      });
    }
    if (!environment.production || this.user && this.user.role === 'super-admin') {
      console.log('filters judgement: ', response);
    }

    return response;
  }

  canTaskBeAddedOnCurrentPage(dataSource: MatTableDataSource<Task>, task: Task): boolean {
    const first: Task = _.first(dataSource.data),
      last: Task = _.last(dataSource.data);
    let response: boolean = true;

    if (!environment.production || this.user && this.user.role === 'super-admin') {
      console.log(this.routeParams);
      console.log(task, dataSource.data, last);
    }

    // check if task should be shown on current page
    if (response && typeof first !== 'undefined' && typeof last !== 'undefined') {
      if (this.routeParams.page === 1 || this.routeParams.page === null || typeof this.routeParams.page === 'undefined') {
        if (this.routeParams.direction === 'desc' || this.routeParams.direction === '' || this.routeParams.direction === null) {
          if (!environment.production || this.user && this.user.role === 'super-admin') {
            console.log('task: ', task.created_at);
            console.log('last: ', last.created_at);
          }
          if (last.created_at > task.created_at) {
            if (!environment.production || this.user && this.user.role === 'super-admin') {
              console.log('11');
            }
            response = false;
          }
        } else {
          if (!environment.production || this.user && this.user.role === 'super-admin') {
            console.log('first: ', first.created_at);
            console.log('task: ', task.created_at);
          }
          if (first.created_at < task.created_at) {
            if (!environment.production || this.user && this.user.role === 'super-admin') {
              console.log('12');
            }
            response = false;
          }
        }
      } else {
        if (!environment.production || this.user && this.user.role === 'super-admin') {
          console.log('first: ', first.created_at);
          console.log('task: ', task.created_at);
          console.log('last: ', last.created_at);
        }
        if (this.routeParams.direction === 'desc' || this.routeParams.direction === '' || this.routeParams.direction === null) {
          if (first.created_at < task.created_at || last.created_at > task.created_at) {
            if (!environment.production || this.user && this.user.role === 'super-admin') {
              console.log('13');
            }
            response = false;
          }
        } else {
          if (first.created_at > task.created_at || last.created_at < task.created_at) {
            if (!environment.production || this.user && this.user.role === 'super-admin') {
              console.log('14');
            }
            response = false;
          }
        }
      }
    }
    if (!environment.production || this.user && this.user.role === 'super-admin') {
      console.log('page judgement: ', response);
    }

    return response;
  }

  canTaskBeAddedForCurrentUser(user: User, task: Task): boolean {
    return (this.router.url.indexOf('/taken/alle') !== -1 && user.permissions.some((p: string): boolean => ['tasks.view.all'].includes(p))) ||
    this.router.url.indexOf('/taken/gesloten') !== -1 && (
      user.permissions.some((p: string): boolean => ['tasks.view'].includes(p)) &&
      ['resolved', 'rejected'].indexOf(task.status.name) !== -1 && user.id === task.assigned_user_id
    ) ||
    (
      this.router.url.indexOf('/taken/gesloten') === -1 &&
      this.router.url.indexOf('/taken/alle') === -1 &&
      user.permissions.some((p: string): boolean => ['tasks.view'].includes(p)) &&
      ['to-to', 'assigned', 'in-progress', 'paused', 'review', 'reopened', 'review-failed'].indexOf(task.status.name) !== -1 &&
          user.id === task.assigned_user_id
    );
  }

  getMissingData(dataSource: MatTableDataSource<Task>): void {
    const start: number = dataSource.data.length,
      filterPageName: string = this.api.filterPageName;

    this.api.filterPageName = null;
    this.taskService.missing('open', start).subscribe((response: ServerResponse): void => {
      if (typeof response.data !== 'undefined' && typeof response.data[0] !== 'undefined') {
        dataSource.data.push(response.data[0]);

        dataSource.data = dataSource.data;
      }

      this.api.filterPageName = filterPageName;
    });
  }

  clear(): void {
    this.taskUpdated.next(null);
    this.taskDeleted.next(null);
  }

  destroy(): void {
    this.clearBroadcastEvents.forEach((event: string): void => this.broadcastService.clearEventSubject(event));
  }
}
